import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { EligibilityCandidate } from '@kp/eligibility/eligibility-candidate.model';
import { Constants } from '@kp/shared/constants.service';
import { WorkbookBloc } from '@kp/workbook/workbook.bloc';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface WorkbookEligibilityFormValues {
  firstName: string;
  lastName: string;
  streetAddress: string;
  apt: string;
  city: string;
  state: string;
  postalCode: string;
}

@Component({
  selector: 'kp-screen-workbook-edit',
  templateUrl: './screen-workbook-edit.component.html',
  styleUrls: ['./screen-workbook-edit.component.scss'],
})
export class ScreenWorkbookEditComponent implements OnInit, OnDestroy {
  constants = Constants;
  apt: UntypedFormControl;
  city: UntypedFormControl;
  firstName: UntypedFormControl;
  lastName: UntypedFormControl;
  postalCode: UntypedFormControl;
  state: UntypedFormControl;
  streetAddress: UntypedFormControl;
  workbookAddressForm: UntypedFormGroup;

  private _destroy$ = new Subject<void>();
  private _eligibilityCandidate: EligibilityCandidate;

  constructor(private _analyticsBloc: AnalyticsBloc, private _router: Router, private _workbookBloc: WorkbookBloc) {}

  ngOnInit() {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.workbook,
        event: AnalyticEvent.workbook_edit_shown,
      })
    );
    this.streetAddress = new UntypedFormControl('', Validators.required);
    this.apt = new UntypedFormControl('');
    this.city = new UntypedFormControl('', Validators.required);
    this.state = new UntypedFormControl('', Validators.required);
    this.postalCode = new UntypedFormControl('', Validators.pattern(/^(\d{5})$/));
    this.firstName = new UntypedFormControl('');
    this.lastName = new UntypedFormControl('');

    this.workbookAddressForm = new UntypedFormGroup({
      firstName: this.firstName,
      lastName: this.lastName,
      streetAddress: this.streetAddress,
      apt: this.apt,
      city: this.city,
      state: this.state,
      postalCode: this.postalCode,
    });

    this._workbookBloc.workbookCandidate$.pipe(takeUntil(this._destroy$)).subscribe((eligibilityCandidate) => {
      if (eligibilityCandidate) {
        this._eligibilityCandidate = eligibilityCandidate;
        this.firstName.setValue(eligibilityCandidate.firstName);
        this.lastName.setValue(eligibilityCandidate.lastName);
        this.streetAddress.setValue(eligibilityCandidate.addressLine);
        this.apt.setValue(eligibilityCandidate.apt);
        this.city.setValue(eligibilityCandidate.city);
        this.state.setValue(eligibilityCandidate.region || '');
        this.postalCode.setValue(eligibilityCandidate.postalCode);
      }
    });
  }

  ngOnDestroy() {
    this.workbookAddressForm?.reset?.();
    this._destroy$.next();
    this._destroy$.complete();
  }

  onSubmit(formValues: WorkbookEligibilityFormValues) {
    const eligibilityCandidate = new EligibilityCandidate().deserialize({
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      addressLine: formValues.streetAddress,
      apt: formValues.apt,
      city: formValues.city,
      region: formValues.state,
      postalCode: formValues.postalCode,
      email: this._eligibilityCandidate.email,
      eligibilityLink: this._eligibilityCandidate.eligibilityLink,
    });
    this._workbookBloc.updateCandidate(eligibilityCandidate);
    return this._router.navigate(['/welcome/workbook/review']);
  }
}
